import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  Input,
  AvatarComponent,
  Progress,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import { formatDate } from "@learn-me/utils";
import {
  Search,
  Users,
  Eye,
  UserPlus,
  Mail,
  Clock,
  CheckCircle,
  Send,
} from "lucide-react";
import {
  StudentStatusBadge,
  InviteStudentModal,
  InvitationsList,
} from "@/features/admin/components/students";

type StatusFilter = "all" | "active" | "inactive";

export default function StudentsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-students-enhanced", page, search, statusFilter],
    queryFn: () =>
      adminApi.getStudentsEnhanced(page, 20, {
        search: search || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
      }),
  });

  const { data: invitationStats } = useQuery({
    queryKey: ["admin-invitation-stats"],
    queryFn: adminApi.getInvitationStats,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Estudiantes</h1>
          <p className="text-muted-foreground">
            Gestiona usuarios e invitaciones de la plataforma.
          </p>
        </div>
        <Button onClick={() => setInviteModalOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Invitar Estudiante
        </Button>
      </div>

      <Tabs defaultValue="students" className="space-y-4">
        <TabsList>
          <TabsTrigger value="students" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Estudiantes
            {data?.pagination.total !== undefined && (
              <Badge variant="secondary" className="ml-1">
                {data.pagination.total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Invitaciones
            {invitationStats?.pending !== undefined &&
              invitationStats.pending > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {invitationStats.pending}
                </Badge>
              )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <form
              onSubmit={handleSearch}
              className="flex gap-2 flex-1 min-w-[300px] max-w-md"
            >
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Buscar por nombre o email..."
                  className="pl-10"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <Button type="submit">Buscar</Button>
            </form>

            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as StatusFilter);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="inactive">Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4 flex gap-4">
                    <div className="w-12 h-12 bg-muted rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-muted rounded w-1/4" />
                      <div className="h-4 bg-muted rounded w-1/3" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : !data?.students?.length ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">
                  No se encontraron estudiantes
                </h2>
                <p className="text-muted-foreground">
                  {search || statusFilter !== "all"
                    ? "Intenta con otros filtros de busqueda."
                    : "Aun no hay usuarios registrados. Invita a tu primer estudiante."}
                </p>
                {!search && statusFilter === "all" && (
                  <Button
                    className="mt-4"
                    onClick={() => setInviteModalOpen(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invitar Estudiante
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="space-y-4">
                {data.students.map((student) => {
                  const fullName =
                    `${student.firstName} ${student.lastName}`.trim();
                  return (
                    <Card key={student.id}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <AvatarComponent
                          src={student.avatarUrl ?? undefined}
                          alt={fullName}
                          fallback={student.firstName?.charAt(0) || "?"}
                          size="lg"
                        />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{fullName}</h3>
                            <StudentStatusBadge isActive={student.isActive} />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {student.email}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Registrado:{" "}
                            {formatDate(new Date(student.createdAt))}
                          </p>
                        </div>

                        <div className="text-center px-4">
                          <p className="text-2xl font-bold">
                            {student.enrollmentsCount}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Cursos
                          </p>
                        </div>

                        <div className="text-center px-4">
                          <p className="text-2xl font-bold">
                            {student.purchasesCount}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Compras
                          </p>
                        </div>

                        <div className="w-48">
                          {student.enrollments.length > 0 && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1 truncate">
                                {student.enrollments[0].course.title}
                              </p>
                              <Progress
                                value={student.enrollments[0].progress}
                                className="h-2"
                              />
                            </div>
                          )}
                        </div>

                        <Link to={`/admin/students/${student.id}`}>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4 mr-2" />
                            Ver detalles
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Pagination */}
              {data.pagination.totalPages > 1 && (
                <div className="flex justify-center gap-2">
                  <Button
                    variant="outline"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="flex items-center px-4">
                    Pagina {page} de {data.pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    disabled={page === data.pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="invitations" className="space-y-4">
          {/* Invitation Stats */}
          {invitationStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Send className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {invitationStats.total}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Total enviadas
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {invitationStats.pending}
                    </p>
                    <p className="text-xs text-muted-foreground">Pendientes</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {invitationStats.accepted}
                    </p>
                    <p className="text-xs text-muted-foreground">Aceptadas</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Clock className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {invitationStats.expired}
                    </p>
                    <p className="text-xs text-muted-foreground">Expiradas</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <InvitationsList />
        </TabsContent>
      </Tabs>

      <InviteStudentModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
      />
    </div>
  );
}
